import {StickyNoteData} from '@dv5/domain'
import React, {CSSProperties, FC, useEffect, useRef, useState} from 'react'
import {isModPressed} from '@dv5/components'
import {ColorPicker} from '../common/ColorPicker'
import {ReactionPicker} from '../common/ReactionPicker'
import {COLOR_PRESETS} from "./constants"
import {FontStylePicker} from "./FontStylePicker"
import {useStickyNoteUpdate} from "./useStickyNoteUpdate"
import './StickyNoteFullEditor.less'

interface Props {
    canvasId: string
    widgetId: string
    data: StickyNoteData
    toolbarStyles: CSSProperties
    onClose: () => void
}

export const StickyNoteFullEditor: FC<Props> = ({
    canvasId,
    widgetId,
    data,
    toolbarStyles,
    onClose,
}) => {
    const [editingText, setEditingText] = useState(data.text)
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    useEffect(() => {
        setEditingText(data.text)
    }, [data.text])

    const { updateData } = useStickyNoteUpdate(canvasId, widgetId, data)

    const handleColorChange = (color: string) => {
        updateData(['bgColor'], {bgColor: color})
    }

    const handleFontStyleChange = (fontStyle: string) => {
        updateData(['fontStyle'], {fontStyle})
    }

    const handleReactionChange = (reactionType: string) => {
        updateData(['reactionType'], {reactionType})
    }

    const saveText = () => {
        updateData(['text'], {text: editingText})
    }

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEditingText(e.target.value)
    }

    const handleTextKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Escape') {
            onClose()
        } else if (e.key === 'Enter' && isModPressed(e)) {
            e.preventDefault()
            saveText()
        }
    }

    const closeEditor = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        event.preventDefault()
        onClose()
    }

    return (
        <div className='sticky-note-full-editor' >
            <div className='sticky-note-full-editor__header'>
                <span className='sticky-note-full-editor__title'>Sticky Note Properties</span>
                <button
                    className='sticky-note-full-editor__close'
                    onMouseDown={closeEditor}
                    aria-label='Close full editor'
                    type='button'
                >
                    ×
                </button>
            </div>

            <div className='sticky-note-full-editor__sections'>
                <div className='sticky-note-full-editor__section sticky-note-full-editor__section--color' title='Background'>
                    <ColorPicker
                        value={data.bgColor}
                        presets={COLOR_PRESETS}
                        onChange={handleColorChange}
                    />
                </div>

                <div className='sticky-note-full-editor__section sticky-note-full-editor__section--font-style' title='Font Style'>
                    <FontStylePicker
                        prefix="sticky-note-full-editor"
                        value={data.fontStyle}
                        onChange={handleFontStyleChange}
                    />
                </div>

                <div className='sticky-note-full-editor__section sticky-note-full-editor__section--reaction' title='Reaction'>
                    <ReactionPicker
                        value={data.reactionType}
                        allowEmpty={true}
                        onChange={handleReactionChange}
                    />
                </div>

                <div className='sticky-note-full-editor__section sticky-note-full-editor__section--text' title='Text'>
                    <textarea
                        ref={textareaRef}
                        className='sticky-note-full-editor__textarea'
                        value={editingText}
                        onChange={handleTextChange}
                        onKeyDown={handleTextKeyDown}
                        onBlur={saveText}
                        rows={3}
                        style={{
                            fontStyle: data.fontStyle as 'normal' | 'italic',
                        }}
                    />
                </div>
            </div>
        </div>
    )
}
